import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';

const AUTO_CLOSE_SECONDS = 5;

const CTA_CLASS =
    'flex h-14 w-full items-center justify-center rounded-full bg-[#181818] text-[1.25rem] font-medium tracking-[-0.02em] text-[#f3f3f3] shadow-[0_0.1875rem_0.8125rem_0_rgba(0,0,0,0.25),0_1.25rem_2.125rem_0_rgba(0,0,0,0.25)] transition-colors hover:bg-[#2c2c2c]';

interface Step8DoneProps {
    onClose: () => void;
    email?: string;
    /** The noun that completes "match you with 2–3 …" — e.g. "professionals", "projects", "products and suppliers". */
    matchSubjectPlural: string;
    /**
     * An account was created on the way here. The screen then explains where
     * the request lives — a brand-new member has never seen the portal — and
     * holds itself open instead of auto-closing.
     */
    accountCreated?: boolean;
}

const ReviewCopy = ({
    matchSubjectPlural,
}: Pick<Step8DoneProps, 'matchSubjectPlural'>) => (
    <>
        <p className="text-[1.1875rem] leading-snug font-medium tracking-[-0.02em] text-[#181818]">
            Our team will review your brief and personally match you with 2–3{' '}
            {matchSubjectPlural} whose work, availability and approach align
            with your project.
        </p>
        <p className="mt-4 text-[1.1875rem] leading-snug font-medium tracking-[-0.02em] text-[#181818]">
            You&rsquo;ll hear from us within 2 business days.
        </p>
    </>
);

/** Known-account confirmation: short, then closes itself. */
const ExistingUserSuccess = ({
    onClose,
    email,
    matchSubjectPlural,
}: Omit<Step8DoneProps, 'accountCreated'>) => {
    const [secondsLeft, setSecondsLeft] = useState(AUTO_CLOSE_SECONDS);

    useEffect(() => {
        if (secondsLeft <= 0) {
            onClose();
            return;
        }
        const timer = setTimeout(() => {
            setSecondsLeft((s) => s - 1);
        }, 1000);
        return () => clearTimeout(timer);
    }, [secondsLeft, onClose]);

    return (
        <div className="mx-auto flex min-h-0 w-full max-w-86 flex-1 flex-col">
            <h2 className="mt-9 flex items-center gap-2.5 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
                Success
                <Check className="size-6 shrink-0" strokeWidth={2} />
            </h2>
            <p className="mt-3 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
                Your brief has been sent successfully.
            </p>
            <div className="mt-8">
                <ReviewCopy matchSubjectPlural={matchSubjectPlural} />
            </div>
            {email && (
                <p className="mt-4 text-[1.1875rem] leading-snug tracking-[-0.02em] text-[#828282]">
                    A confirmation has been sent to {email}.
                </p>
            )}
            {/* Spreads the card, which has a fixed height, so the action
                holds the bottom edge; the sheet hugs its content and this
                collapses to nothing there. */}
            <div className="flex-1" />
            <GateStickyFooter className="mt-5" desktopClassName="mb-2">
                <button type="button" onClick={onClose} className={CTA_CLASS}>
                    Close
                </button>
                <p className="mt-4 text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]">
                    Closing in {secondsLeft}...
                </p>
            </GateStickyFooter>
        </div>
    );
};

/** Fresh-account confirmation: says where the request now lives, and stays put. */
const NewUserSuccess = ({
    onClose,
    email,
    matchSubjectPlural,
}: Omit<Step8DoneProps, 'accountCreated'>) => (
    <div className="mx-auto my-auto flex w-full max-w-86 flex-col">
        <h2 className="flex items-center gap-2.5 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
            Brief sent
            <Check className="size-6 shrink-0" strokeWidth={2} />
        </h2>
        <div className="mt-5">
            <ReviewCopy matchSubjectPlural={matchSubjectPlural} />
        </div>
        <p className="mt-4 text-[1.1875rem] leading-snug tracking-[-0.02em] text-[#828282]">
            Track the status of your request any time in your{' '}
            {/* Plain anchor on purpose: a full load closes the modal rather
                than soft-navigating under the open dialog. */}
            <a
                href="/member/get-matched-requests"
                className="text-[#181818] underline"
            >
                member portal
            </a>
            .{email && <> A confirmation has been sent to {email}.</>}
        </p>
        <GateStickyFooter className="mt-8">
            <button type="button" onClick={onClose} className={CTA_CLASS}>
                Close
            </button>
        </GateStickyFooter>
    </div>
);

/**
 * Success step of the brief, in the enquiry gate's success language: heading
 * with a tick, the review promise, a secondary line, one wide CTA. A known
 * account gets the short self-closing confirmation; a just-created one is told
 * where the request lives and keeps the screen until it closes it.
 */
export const Step8Done = ({
    onClose,
    email,
    matchSubjectPlural,
    accountCreated,
}: Step8DoneProps) =>
    accountCreated ? (
        <NewUserSuccess
            onClose={onClose}
            email={email}
            matchSubjectPlural={matchSubjectPlural}
        />
    ) : (
        <ExistingUserSuccess
            onClose={onClose}
            email={email}
            matchSubjectPlural={matchSubjectPlural}
        />
    );
